import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

const AdminLiveSettings = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    livestream_url: "",
    title: "RGM Studio Live",
    description: "",
    schedule_text: "Wed Review Night • Sun Spotlight",
    next_live_date: "",
    is_live: false,
    youtube_url: "",
    facebook_url: "",
    instagram_url: "",
    tiktok_url: "",
    twitch_url: "",
    custom_url: "",
    custom_label: ""
  });
  const navigate = useNavigate();

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get(`/live/admin/settings`);
      const data = response.data;
      
      setFormData({
        livestream_url: data.livestream_url || "",
        title: data.title || "RGM Studio Live",
        description: data.description || "",
        schedule_text: data.schedule_text || "Wed Review Night • Sun Spotlight",
        next_live_date: data.next_live_date ? new Date(data.next_live_date).toISOString().slice(0, 16) : "",
        is_live: data.is_live || false,
        youtube_url: data.youtube_url || "",
        facebook_url: data.facebook_url || "",
        instagram_url: data.instagram_url || "",
        tiktok_url: data.tiktok_url || "",
        twitch_url: data.twitch_url || "",
        custom_url: data.custom_url || "",
        custom_label: data.custom_label || ""
      });
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get(`/auth/me`);
      if (response.data.role !== "admin" && response.data.role !== "moderator") {
        toast.error("Admin access required");
        navigate('/dashboard');
        return;
      }
      setUser(response.data);
      await fetchSettings();
    } catch (error) {
      navigate('/login');
    }
  }, [navigate, fetchSettings]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const _unused_check_auth_legacy = async () => {
    try {
      const response = await api.get(`/auth/me`);
      if (response.data.role !== "admin" && response.data.role !== "moderator") {
        toast.error("Admin access required");
        navigate('/dashboard');
        return;
      }
      setUser(response.data);
      await fetchSettings();
    } catch (error) {
      navigate('/login');
    }
  };

  const _unused_fetch_settings_legacy = async () => {
    try {
      const response = await api.get(`/live/admin/settings`);
      const data = response.data;
      setFormData({
        livestream_url: data.livestream_url || "",
        title: data.title || "RGM Studio Live",
        description: data.description || "",
        schedule_text: data.schedule_text || "Wed Review Night • Sun Spotlight",
        next_live_date: data.next_live_date ? new Date(data.next_live_date).toISOString().slice(0, 16) : "",
        is_live: data.is_live || false,
        youtube_url: data.youtube_url || "",
        facebook_url: data.facebook_url || "",
        instagram_url: data.instagram_url || "",
        tiktok_url: data.tiktok_url || "",
        twitch_url: data.twitch_url || "",
        custom_url: data.custom_url || "",
        custom_label: data.custom_label || ""
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        next_live_date: formData.next_live_date ? new Date(formData.next_live_date).toISOString() : null
      };

      await api.post(`/live/settings`, submitData);
      toast.success("Live settings updated successfully!");
    } catch (error) {
      toast.error("Failed to update settings", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a1e' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a1a1e' }}>
      {/* Header */}
      <div className="border-b border-gray-800" style={{ background: '#0f0f11' }}>
        <div className="container-custom">
          <div className="flex items-center justify-between py-4">
            <h1 className="text-2xl font-bold text-white" data-testid="admin-live-title">
              Live Stream Settings
            </h1>
            <div className="flex gap-3">
              <a href="/live" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="text-purple-400 border-purple-500">
                  View Live Page
                </Button>
              </a>
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="text-white border-gray-700"
                data-testid="back-to-admin"
              >
                Back to Admin
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="max-w-3xl mx-auto">
          <Card className="p-8" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Livestream URL */}
              <div>
                <Label htmlFor="livestream_url" className="text-gray-300">
                  YouTube Live URL *
                </Label>
                <Input
                  id="livestream_url"
                  value={formData.livestream_url}
                  onChange={(e) => setFormData({ ...formData, livestream_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="form-input mt-1"
                  data-testid="input-livestream-url"
                />
                <p className="text-sm text-gray-400 mt-1">
                  Paste your YouTube Live stream URL or video URL
                </p>
              </div>

              {/* Title */}
              <div>
                <Label htmlFor="title" className="text-gray-300">
                  Page Title
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="form-input mt-1"
                  data-testid="input-title"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description" className="text-gray-300">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="form-textarea mt-1"
                  placeholder="Tell viewers what to expect..."
                  data-testid="input-description"
                />
              </div>

              {/* Schedule Text */}
              <div>
                <Label htmlFor="schedule_text" className="text-gray-300">
                  Schedule Text
                </Label>
                <Input
                  id="schedule_text"
                  value={formData.schedule_text}
                  onChange={(e) => setFormData({ ...formData, schedule_text: e.target.value })}
                  placeholder="Wed Review Night • Sun Spotlight"
                  className="form-input mt-1"
                  data-testid="input-schedule"
                />
              </div>

              {/* Next Live Date */}
              <div>
                <Label htmlFor="next_live_date" className="text-gray-300">
                  Next Live Date & Time
                </Label>
                <Input
                  id="next_live_date"
                  type="datetime-local"
                  value={formData.next_live_date}
                  onChange={(e) => setFormData({ ...formData, next_live_date: e.target.value })}
                  className="form-input mt-1"
                  data-testid="input-next-live-date"
                />
                <p className="text-sm text-gray-400 mt-1">
                  Used for countdown timer on the live page
                </p>
              </div>

              {/* Is Live Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_live"
                  checked={formData.is_live}
                  onChange={(e) => setFormData({ ...formData, is_live: e.target.checked })}
                  className="w-5 h-5"
                  data-testid="input-is-live"
                />
                <Label htmlFor="is_live" className="text-gray-300">
                  Show "LIVE" badge on the page
                </Label>
              </div>

              {/* Phase 3.2: Per-platform Live Links */}
              <div className="border-t border-gray-800 pt-6">
                <h3 className="text-white font-bold mb-1">Social Live Links</h3>
                <p className="text-xs text-gray-400 mb-4">
                  These appear as "Join Live" buttons on the public Live page. Leave any field blank to hide it.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="youtube_url" className="text-gray-300">YouTube Live</Label>
                    <Input
                      id="youtube_url"
                      value={formData.youtube_url}
                      onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                      placeholder="https://youtube.com/live/..."
                      className="form-input mt-1"
                      data-testid="input-youtube-live"
                    />
                  </div>
                  <div>
                    <Label htmlFor="facebook_url" className="text-gray-300">Facebook Live</Label>
                    <Input
                      id="facebook_url"
                      value={formData.facebook_url}
                      onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                      placeholder="https://facebook.com/.../live"
                      className="form-input mt-1"
                      data-testid="input-facebook-live"
                    />
                  </div>
                  <div>
                    <Label htmlFor="instagram_url" className="text-gray-300">Instagram Live</Label>
                    <Input
                      id="instagram_url"
                      value={formData.instagram_url}
                      onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                      placeholder="https://instagram.com/..."
                      className="form-input mt-1"
                      data-testid="input-instagram-live"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tiktok_url" className="text-gray-300">TikTok Live</Label>
                    <Input
                      id="tiktok_url"
                      value={formData.tiktok_url}
                      onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
                      placeholder="https://tiktok.com/@.../live"
                      className="form-input mt-1"
                      data-testid="input-tiktok-live"
                    />
                  </div>
                  <div>
                    <Label htmlFor="twitch_url" className="text-gray-300">Twitch Live</Label>
                    <Input
                      id="twitch_url"
                      value={formData.twitch_url}
                      onChange={(e) => setFormData({ ...formData, twitch_url: e.target.value })}
                      placeholder="https://twitch.tv/..."
                      className="form-input mt-1"
                      data-testid="input-twitch-live"
                    />
                  </div>
                  <div>
                    <Label htmlFor="custom_url" className="text-gray-300">Custom Link</Label>
                    <Input
                      id="custom_url"
                      value={formData.custom_url}
                      onChange={(e) => setFormData({ ...formData, custom_url: e.target.value })}
                      placeholder="https://twitch.tv/..."
                      className="form-input mt-1"
                      data-testid="input-custom-live"
                    />
                  </div>
                  <div>
                    <Label htmlFor="custom_label" className="text-gray-300">Custom Link Label</Label>
                    <Input
                      id="custom_label"
                      value={formData.custom_label}
                      onChange={(e) => setFormData({ ...formData, custom_label: e.target.value })}
                      placeholder="Twitch"
                      className="form-input mt-1"
                      data-testid="input-custom-label"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              {formData.livestream_url && (
                <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded">
                  <p className="text-sm text-gray-300 mb-2">Preview URL:</p>
                  <p className="text-purple-400 text-sm break-all">{formData.livestream_url}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={saving}
                  className="submit-music-btn"
                  data-testid="save-settings-btn"
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
                <Button
                  type="button"
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  className="text-white border-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>

          {/* Instructions */}
          <Card className="p-6 mt-6" style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}>
            <h3 className="text-lg font-semibold text-white mb-3">How to Use:</h3>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li>• Go live on YouTube via StreamYard or YouTube Studio</li>
              <li>• Copy your YouTube Live stream URL</li>
              <li>• Paste it in the "YouTube Live URL" field above</li>
              <li>• Set the next live date/time for the countdown timer</li>
              <li>• Check "Show LIVE badge" when you're streaming</li>
              <li>• Click "Save Settings" to update the live page</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminLiveSettings;
